// src/components/auth/Login.js
import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { userService } from "../../services/userService";
import { companyService } from "../../services/companyService";
import { logAuditEvent } from "../../utils/logAudit";
import { normalizeRole, ROLES } from "../../utils/roles";
import Header from "../Header/Header";
import styles from "./Auth.module.css";

const Login = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const { login, setUserProfile } = useAuth();

 
  const getSafeRedirect = () => {
    const params = new URLSearchParams(location.search);
    const raw = params.get("redirect");
    if (!raw) return null;
    try {
      const decoded = decodeURIComponent(raw);
      
      if (
        !decoded.startsWith("/") ||
        decoded.startsWith("//") ||
        decoded.startsWith("/\\")
      ) {
        return null;
      }
      return decoded;
    } catch {
      return null;
    }
  };
  useEffect(() => {
    document.body.style.overflow = "hidden";
    document.body.style.position = "fixed";
    document.body.style.width = "100%";

    document.body.removeAttribute("data-theme");
    document.documentElement.removeAttribute("data-theme");

    return () => {
      document.body.style.overflow = "";
      document.body.style.position = "";
      document.body.style.width = "";
    };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setIsLoading(true);

    const trimmedEmail = email.trim();

    if (!trimmedEmail || !password) {
      setError("Please enter both your email and password.");
      setIsLoading(false);
      return;
    }

    try {
      // 1. Authenticate credentials
      const authProfile = await login(trimmedEmail, password);
      const targetUserId = authProfile?.uid || authProfile?.id || authProfile?.userId;

      if (!targetUserId) {
        throw new Error("Unable to identify authenticated user ID.");
      }

      // 2. Fetch full DB User Profile details (with companyId)
      let userData = await userService.fetchUserById(targetUserId);

      if (!userData) {
        userData = userService.buildFallbackProfile(authProfile);
      }

      if (!userData) {
        throw new Error("User profile not found in database. Please contact an administrator.");
      }

      // 3. Put full DB user into AuthContext state AND localStorage
      setUserProfile(userData);

      // 4. Fetch Company Tenant data if companyId exists
      let companyData = null;
      if (userData.companyId) {
        try {
          const fetchCompany = companyService.getCompanyById || companyService.fetchCompanyById;
          if (typeof fetchCompany === "function") {
            companyData = await fetchCompany.call(companyService, userData.companyId);
          }
          if (companyData) {
            localStorage.setItem("companyData", JSON.stringify(companyData));
          } else {
            localStorage.removeItem("companyData");
          }
        } catch (compErr) {
          console.warn("[Login] Could not fetch company profile:", compErr);
          localStorage.removeItem("companyData");
        }
      } else {
        localStorage.removeItem("companyData");
      }

      // 5. Audit Log (non-blocking)
      logAuditEvent({
        actionType: "login",
        entityType: "User",
        entityId: targetUserId,
        description: `User logged in: ${trimmedEmail}`,
        companyId: userData.companyId || null,
      }).catch((err) => console.error("Non-blocking audit log error:", err));
      // 6. Routing — prefer ?redirect= if present and safe
      const redirectTo = getSafeRedirect();
      const normalizedUserRole = normalizeRole(userData.role);

      // Super-admin always goes to their dedicated console — never redirect.
      if (normalizedUserRole === ROLES.SUPERADMIN) {
        navigate("/super-admin");
        return;
      }

      // If login was triggered from a QR scan (redirect present), honour it
      // BEFORE role-based routing — the asset view handles the role logic.
      if (redirectTo) {
        navigate(redirectTo, { replace: true });
        return;
      }

      const hasLocalSetup =
        Boolean(companyData?.localSupabaseURL) &&
        Boolean(companyData?.localSupabaseAnonKey);

      if (hasLocalSetup) {
        navigate("/server-check");
      } else {
        switch (normalizedUserRole) {
          case ROLES.ADMIN:
          case ROLES.LOCATION_ADMIN:
            navigate("/admin");
            break;
          case ROLES.AUDITOR:
            navigate("/auditor");
            break;
          case ROLES.USER:
            navigate("/user");
            break;
            case ROLES.TECHNICIAN:
  navigate("/technician");
  break;
          default:
            navigate("/");
        }
      }
    } catch (err) {
      logAuditEvent({
        actionType: "login_attempt",
        entityType: "User",
        description: `Failed login attempt with email: ${trimmedEmail}`,
        companyId: null,
      }).catch(() => {});

      console.error("Login error:", err);
      let errorMessage = "An unexpected error occurred. Please try again.";

      if (err && err.code) {
        switch (err.code) {
          case "auth/user-not-found":
          case "auth/wrong-password":
          case "auth/invalid-credential":
          case "auth/invalid-login-credentials":
            errorMessage =
              "Invalid email or password. Please check your credentials and try again.";
            break;
          case "auth/user-disabled":
            errorMessage =
              "This account has been disabled. Please contact an administrator.";
            break;
          case "auth/too-many-requests":
            errorMessage = "Too many failed attempts. Please try again later.";
            break;
          case "auth/network-request-failed":
            errorMessage =
              "Network error. Please check your internet connection.";
            break;
          case "auth/account-setup-incomplete":
            errorMessage =
              "Account setup is incomplete. Please contact an administrator.";
            break;
          default:
            if (err.message) errorMessage = err.message;
            break;
        }
      } else if (err && err.message) {
        errorMessage = err.message;
      }

      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Header />
      <div className={styles.loginWrapper}>
        <div className={styles.loginContainer}>
          <div className={styles.loginBox}>
            <div className={styles.logoContainer}>
              <img
                src="/logo.png"
                alt="Niyantrix Logo"
                className={styles.logo}
              />
            </div>

            {error && (
              <div className={styles.errorMessage}>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                  width="16"
                  height="16"
                >
                  <path
                    fillRule="evenodd"
                    d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                    clipRule="evenodd"
                  />
                </svg>
                {error}
              </div>
            )}

            <form className={styles.loginForm} onSubmit={handleSubmit}>
              <div className={styles.formGroup}>
                <label htmlFor="email">Email address</label>
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoComplete="email"
                  placeholder="Enter your email"
                />
              </div>

              <div className={styles.formGroup}>
                <label htmlFor="password">Password</label>
                <div className={styles.passwordContainer}>
                  <input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    autoComplete="current-password"
                    placeholder="Enter your password"
                  />
                  <button
                    type="button"
                    className={styles.passwordToggle}
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? "Hide" : "Show"}
                  </button>
                </div>
              </div>

              <button
                type="submit"
                className={styles.loginButton}
                disabled={isLoading}
              >
                {isLoading ? "Signing in..." : "Login"}
              </button>
            </form>

            <div className={styles.loginFooter}>
              <p>
                Don't have an account?{" "}
                <Link to="/admin-signup" className={styles.link}>
                  Sign Up
                </Link>
                {" or "}
                <Link to="/request-access" className={styles.link}>
                  Request Access
                </Link>
              </p>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Login;